import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  CircularProgress,
  Card,
  Tooltip,
} from '@mui/material';
import { gql, useQuery, useLazyQuery } from '@apollo/client';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';

const GET_KYC_LOGS = gql`
  query GetKycLogs($limit: Int, $offset: Int) {
    getKycLogs(limit: $limit, offset: $offset) {
      success
      data {
        log_id
        log_date
        vin
        imei
        engine_number
        action
        kyc_id
        kyc_status
      }
      count
      error
    }
  }
`;

interface KycLog {
  log_id: number;
  log_date: string | number;
  vin: string;
  imei: string;
  engine_number: string;
  action: string;
  kyc_id: number;
  kyc_status: string;
}

interface QueryResponse {
  getKycLogs: {
    success: boolean;
    data: KycLog[];
    count: number;
    error: string | null;
  };
}

interface QueryVariables {
  limit: number;
  offset: number;
}

const formatDateFromEpoch = (value: string | number | null): string => {
  if (typeof value === 'number') {
    const date = new Date(value * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
  return value || 'N/A';
};

const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const KycLogs = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const tableColumns = [
    "Sr No.",
    // "Log ID",
    // "KYC ID",
    "VIN",
    "IMEI",
    "Engine Number",
    "Date",
    "Action",
    "Status",
  ];

  const { data: kycData, loading: queryLoading } = useQuery<QueryResponse, QueryVariables>(
    GET_KYC_LOGS,
    {
      variables: {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const [exportKycLogs] = useLazyQuery(GET_KYC_LOGS, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (kycData?.getKycLogs) {
      const logs = kycData.getKycLogs.data || [];
      const formattedData = logs.map((log: KycLog, index: number) => ({
        srNo: (page * rowsPerPage) + index + 1,
        ...log
      }));
      setTableData(formattedData);
      setTotalCount(kycData.getKycLogs.count || 0);
    }
  }, [kycData, page, rowsPerPage]);
console.log(kycData?.getKycLogs.count);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return tableData;
    const lowercasedTerm = searchTerm.toLowerCase();
    return tableData.filter((row) =>
      [row.vin, row.imei, row.engine_number, row.action, row.kyc_status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(lowercasedTerm)
    );
  }, [tableData, searchTerm]);

  const handleExportToExcel = async () => {
    try {
      const { data } = await exportKycLogs({
        variables: { offset: 0, limit: 999999 },
      });

      const allLogs = data?.getKycLogs?.data || [];

      const formattedData = allLogs.map((log: KycLog, index: number) => ({
        'Sr No.': index + 1,
        // 'Log ID': log.log_id,
        // 'KYC ID': log.kyc_id,
        'VIN': log.vin,
        'IMEI': log.imei,
        'Engine Number': log.engine_number,
        'Date': formatDateFromEpoch(log.log_date),
        'Action': log.action,
        'Status': log.kyc_status,
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'KYC Logs');
      XLSX.writeFile(workbook, `KYC_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export KYC logs');
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' } }}
            >
              <TextField
                size="small"
                placeholder="Search VIN"
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton sx={{ ml: 1, color: '#000' }} onClick={handleExportToExcel}>
                <Tooltip title="Export to Excel" placement="top" arrow>
                  <CloudDownloadIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 800,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 800, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queryLoading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((row, idx) => (
                      <TableRow
                        key={row.log_id}
                        hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                      >
                        <TableCell align="center" sx={cellStyle}>{row.srNo}</TableCell>
                        {/* <TableCell align="center" sx={cellStyle}>{row.log_id}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.kyc_id}</TableCell> */}
                        <TableCell align="center" sx={cellStyle}>{row.vin}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.imei}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.engine_number}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatDateFromEpoch(row.log_date)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.action}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{row.kyc_status}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default KycLogs;